import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardAdditionalButtons } from 'shared/mui/Card/CardAdditionalButtons/CardAdditionalButtons';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { FilterApplyButton, FilterResetButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsInput } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterChipsInput';
import { FilterIdsValues, FilterIdTypes } from 'shared/mui/Filter/hooks/useIdFilters';
import { CloseIconButton } from 'shared/mui/IconButtons';

import { AlertWrapperStyled } from './FilterIdDialog.styled';

type FilterIdDialogProps = {
  currentIdFilter: FilterIdTypes;
  onClose: () => void;
  onReset: (currentIdFilter: FilterIdTypes) => void;
  notFoundIds?: string[];
  notFoundProviderIds?: string[];
  notFoundMerchantIds?: string[];
  notFoundAnyIds?: string[];
  onSubmit: SubmitHandler<FilterIdsValues>;
  open: boolean;
  omit?: FilterIdTypes[];
  onChange?: (currentFilter: FilterIdTypes) => void;
};

export const FilterIdDialog: React.FC<FilterIdDialogProps> = ({
  open,
  omit,
  currentIdFilter,
  onClose,
  onReset,
  onChange,
  onSubmit,
  notFoundIds,
  notFoundProviderIds,
  notFoundMerchantIds,
  notFoundAnyIds,
}) => {
  const translate = useTypedTranslate();

  const { resetField, handleSubmit, watch } = useFormContext<FilterIdsValues>();

  const idWatched = watch(FilterIdTypes.Id);
  const idMerchantWatched = watch(FilterIdTypes.Merchant);
  const idProviderWatched = watch(FilterIdTypes.Provider);
  const idAnyWatched = watch(FilterIdTypes.Any);

  const filterConfig = {
    [FilterIdTypes.Any]: {
      type: FilterIdTypes.Any,
      toggleLabel: translate('app.filter.idAny'),
      inputLabel: translate('app.filter.anyOrderId'),
      value: idAnyWatched,
      notFoundIds: notFoundAnyIds,
    },
    [FilterIdTypes.Id]: {
      type: FilterIdTypes.Id,
      toggleLabel: 'Kubera',
      inputLabel: 'Kubera ID',
      value: idWatched,
      notFoundIds,
    },
    [FilterIdTypes.Merchant]: {
      type: FilterIdTypes.Merchant,
      toggleLabel: translate('app.filter.merchant'),
      inputLabel: translate('app.filter.idMerchant'),
      value: idMerchantWatched,
      notFoundIds: notFoundMerchantIds,
    },
    [FilterIdTypes.Provider]: {
      type: FilterIdTypes.Provider,
      toggleLabel: translate('app.filter.provider'),
      inputLabel: translate('app.filter.idProvider'),
      value: idProviderWatched,
      notFoundIds: notFoundProviderIds,
    },
  };

  const currentFilterConfig = filterConfig[currentIdFilter];

  const filterConfigItems = Object.values(filterConfig);
  const visibleFilterConfigItems = filterConfigItems.filter(({ type }) => !omit?.includes(type));

  const isDisabled = (type: FilterIdTypes) =>
    visibleFilterConfigItems.some((item) => item.type !== type && Boolean(item.value?.length));

  const hasIds = filterConfigItems.some(({ value }) => Boolean(value?.length));
  const hasAlerts =
    filterConfigItems.some(({ notFoundIds }) => Boolean(notFoundIds?.length)) || !hasIds;

  const onChangeHandler = (_: React.MouseEvent<HTMLElement>, newFilter: FilterIdTypes) => {
    if (!newFilter) {
      return;
    }

    onChange?.(newFilter);
  };

  const onSubmitHandler = (values: FilterIdsValues) => {
    onSubmit(values);
    onClose();
  };

  const onResetHandler = () => {
    onReset(currentIdFilter);
    resetField(currentIdFilter);
    onClose();
  };

  return (
    <Dialog fullWidth onClose={onClose} open={open} width="640px">
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.filter.searchById')}
          titleSize="smallBold"
        />
        <CardAdditionalButtons
          sx={{
            height: 'auto',
            padding: hasIds && !hasAlerts ? '16px 24px 0 24px' : '16px 24px 24px 24px',
          }}
        >
          <ToggleButtonGroup
            aria-label="Platform"
            color="primary"
            exclusive
            onChange={onChangeHandler}
            size="small"
            value={currentIdFilter}
          >
            {visibleFilterConfigItems.map(({ type, toggleLabel }) => (
              <ToggleButton disabled={isDisabled(type)} key={type} value={type}>
                {toggleLabel}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </CardAdditionalButtons>
        {filterConfigItems.map(
          ({ type, notFoundIds }) =>
            Boolean(notFoundIds?.length) && (
              <AlertWrapperStyled key={type}>
                <Alert severity="warning">
                  {translate('app.filter.idNotFound')}: {notFoundIds?.join(' ')}
                </Alert>
              </AlertWrapperStyled>
            ),
        )}
        {!hasIds && (
          <AlertWrapperStyled>
            <Alert severity="info">{translate('app.filter.enterOrderIds')}.</Alert>
          </AlertWrapperStyled>
        )}

        <CardContent>
          <FilterChipsInput
            label={currentFilterConfig.inputLabel}
            name={currentIdFilter}
            notFoundIds={currentFilterConfig.notFoundIds}
          />
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <FilterResetButton
              key="reset"
              label={translate('app.buttons.reset')}
              onClick={onResetHandler}
            />,
            <FilterApplyButton key="apply" type="submit" />,
          ]}
        />
      </form>
    </Dialog>
  );
};
